import {
  useCreateModifiableList,
  useDeleteModifiableList,
  useSaneModifiableList,
  useUpdateModifiableListItem,
  useUpdateModifiableListSequence,
} from "@/api/modifiableList";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/datatable";
import { FormCol } from "@/components/ui/form-col";
import { Input } from "@/components/ui/input";
import { SheetHeader } from "@/components/ui/sheet";
import { getMainLanguage, SupportedLocale } from "@/i18n";
import { ModifiableList as ModifiableListType } from "@/lib/types";
import { ColumnDef } from "@tanstack/react-table";
import {
  CheckIcon,
  LockIcon,
  Pencil,
  PlusIcon,
  Trash2,
  XIcon,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { TableRowActionsMenu } from "@/components/table/TableRowActionsMenu";
import { ACTION_I18N_KEYS } from "@/components/table/action-i18n-keys";

const ModifiableListEditInput = ({
  initialValue,
  onSave,
  onCancel,
  onValueChange,
}: {
  initialValue: string;
  onSave: (value: string) => void;
  onCancel: () => void;
  onValueChange?: (value: string) => void;
}) => {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <Input
      ref={inputRef}
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
        onValueChange?.(e.target.value);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          onSave(value.trim());
        } else if (e.key === "Escape") {
          onCancel();
        }
      }}
      className="text-wrap"
      onClick={(e) => e.stopPropagation()}
    />
  );
};

export const ModifiableListContent = ({
  name,
  listKey,
  onSelectValue,
  lang,
  selectedValues = [],
}: {
  name: string;
  listKey: string;
  onSelectValue: (value: string) => void;
  lang?: SupportedLocale;
  /** Currently selected tags — shown first, including values absent from the list. */
  selectedValues?: readonly string[];
}) => {
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const [newItem, setNewItem] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const editingValueRef = useRef("");

  const modifiableListDeleter = useDeleteModifiableList(listKey);
  const modifiableListAdder = useCreateModifiableList(listKey);
  const modifiableListUpdater = useUpdateModifiableListItem(listKey);
  const modifiableListSequenceMutator =
    useUpdateModifiableListSequence(listKey);

  const { data } = useSaneModifiableList(listKey, lang ?? getMainLanguage());

  const lowerSearch = useMemo(() => search.toLowerCase(), [search]);

  const selectedSet = useMemo(
    () => new Set(selectedValues.filter(Boolean)),
    [selectedValues],
  );

  const filteredData = useMemo(() => {
    const list = data ?? [];
    const byValue = new Map(list.map((item) => [item.value, item]));

    // Selected tags first (including orphans not in the vocabulary).
    const selectedRows: ModifiableListType[] = [];
    for (const value of selectedValues) {
      if (!value) continue;
      if (lowerSearch.length > 0 && !value.toLowerCase().includes(lowerSearch)) {
        continue;
      }
      const existing = byValue.get(value);
      if (existing) {
        selectedRows.push(existing);
      } else {
        selectedRows.push({
          id: `selected-orphan-${value}`,
          instanceId: "",
          listKey,
          lang: lang ?? getMainLanguage(),
          value,
          order: -1,
          isLocked: true,
          createdAt: "",
        });
      }
    }

    const rest = list.filter((item) => {
      if (selectedSet.has(item.value)) return false;
      if (lowerSearch.length > 0) {
        return item.value.toLowerCase().includes(lowerSearch);
      }
      return true;
    });

    return [...selectedRows, ...rest];
  }, [data, selectedValues, selectedSet, lowerSearch, listKey, lang]);

  const handleStartEdit = useCallback((id: string, currentValue: string) => {
    editingValueRef.current = currentValue;
    setEditingId(id);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
  }, []);

  const handleSaveEdit = useCallback(
    (id: string, value: string) => {
      if (value.length > 0) {
        modifiableListUpdater.mutate(
          { id, value },
          {
            onSuccess: () => {
              setEditingId(null);
            },
          },
        );
      }
    },
    [modifiableListUpdater],
  );

  const columns: ColumnDef<ModifiableListType>[] = useMemo(() => {
    return [
      {
        id: name,
        size: 10000,
        cell: (val) => {
          const isEditing = editingId === val.row.original.id;
          const isLocked = val.row.original.isLocked ?? false;
          return isEditing ? (
            <ModifiableListEditInput
              key={val.row.original.id}
              initialValue={val.row.original.value}
              onValueChange={(value) => {
                editingValueRef.current = value;
              }}
              onSave={(value) => handleSaveEdit(val.row.original.id, value)}
              onCancel={handleCancelEdit}
            />
          ) : (
            <div className="flex items-center gap-2">
              {selectedSet.has(val.row.original.value) && (
                <CheckIcon className="size-4 shrink-0 text-primary" />
              )}
              <span className="text-wrap">{val.row.original.value}</span>
              {isLocked && !val.row.original.id.startsWith("selected-orphan-") && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <LockIcon className="size-4 text-slate-500" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {t("itemLocked") ||
                          "This item is locked and cannot be modified"}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          );
        },
      },
      {
        id: "actions",
        meta: { isActionColumn: true },
        size: 0,
        cell: (val) => {
          const isEditing = editingId === val.row.original.id;
          const isLocked = val.row.original.isLocked ?? false;
          return (
            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <Button
                    className="w-fit p-1"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveEdit(
                        val.row.original.id,
                        editingValueRef.current.trim(),
                      );
                    }}
                  >
                    <CheckIcon className="size-4 stroke-slate-600" />
                  </Button>
                  <Button
                    className="w-fit p-1"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelEdit();
                    }}
                  >
                    <XIcon className="size-4 stroke-slate-600" />
                  </Button>
                </>
              ) : (
                <div
                  className="flex justify-end"
                  onClick={(event) => event.stopPropagation()}
                >
                  <TableRowActionsMenu
                    actions={[
                      {
                        id: "edit",
                        label: t(ACTION_I18N_KEYS.edit),
                        icon: Pencil,
                        disabled: isLocked,
                        onSelect: () => {
                          if (!isLocked) {
                            handleStartEdit(
                              val.row.original.id,
                              val.row.original.value,
                            );
                          }
                        },
                      },
                      {
                        id: "delete",
                        label: t(ACTION_I18N_KEYS.delete),
                        icon: Trash2,
                        destructive: true,
                        disabled: isLocked,
                        onSelect: () => {
                          if (!isLocked) {
                            modifiableListDeleter.mutate(val.row.original.id);
                          }
                        },
                      },
                    ]}
                  />
                </div>
              )}
            </div>
          );
        },
      },
    ];
  }, [
    name,
    modifiableListDeleter,
    editingId,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    selectedSet,
    t,
  ]);

  const handleRowDragged = async (newData: ModifiableListType[]) => {
    const newSequence = newData
      .map((d) => d.id)
      .filter((id) => !id.startsWith("selected-orphan-"));
    if (newSequence.length === 0) return;
    await modifiableListSequenceMutator.mutate({
      newSequence,
    });
  };

  return (
    <>
      <SheetHeader className="p-8 py-4 border-b"></SheetHeader>
      <div className="h-full p-8 gap-8 overflow-auto">
        <FormCol>
          <FormCol className="gap-0">
            <label
              htmlFor="addElementListModifiable"
              className="pb-1.5 text-sm"
            >
              {t("addElementListModifiable")}
            </label>
            <div className="flex min-w-0 flex-row gap-2">
              <Input
                name="addElementListModifiable"
                id="addElementListModifiable"
                onChange={(e) => setNewItem(e.target.value)}
                value={newItem}
              />
              <Button
                className="p-2"
                variant="ghost"
                disabled={newItem.length <= 0}
                onClick={() => {
                  modifiableListAdder.mutate(
                    {
                      listKey: listKey,
                      value: newItem,
                      lang: lang ?? getMainLanguage(),
                    },
                    { onSuccess: () => setNewItem("") },
                  );
                }}
              >
                <PlusIcon className="size-4 stroke-slate-600" />
              </Button>
            </div>
          </FormCol>

          <Input
            placeholder={t("search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <h2 className="font-semibold text-xl">{t(name)}</h2>
          <DataTable
            enableDragRows
            onRowDragged={(d) => handleRowDragged(d.newData)}
            slim
            headless
            data={filteredData}
            columns={columns}
            onRowClick={(value) => onSelectValue(value.original.value)}
          />
        </FormCol>
      </div>
    </>
  );
};
